import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import type { RecentHistoryEntry } from './recentHistory';
import { useRecentHistory } from './RecentHistoryProvider';

interface DetailVisit {
  type: RecentHistoryEntry['type'];
  /** Identity from route params (or the selected detail's route-derived prop). */
  id: string | undefined;
  loadedId: string | undefined;
  title: string | null | undefined;
  path: string;
  isSuccess: boolean;
}

/**
 * Record a detail only after its query succeeds for the currently requested ID.
 * This avoids recording stale data during route changes with retained query data.
 * Primitive dependencies avoid repeating the effect on unrelated page updates;
 * the provider deduplicates refetches and remounts within the same detail visit.
 */
const useRecordRecentDetail = ({ type, id, loadedId, title, path, isSuccess }: DetailVisit) => {
  const { recordVisit } = useRecentHistory();
  const { pathname } = useLocation();

  useEffect(() => {
    if (!isSuccess || !id || loadedId !== id || !title?.trim()) return;
    // Selection-driven content can briefly retain the previous detail on navigation.
    if (pathname !== path && !pathname.startsWith(`${path}/`)) return;
    recordVisit({ type, id, title: title.trim(), path });
  }, [type, id, loadedId, title, path, pathname, isSuccess, recordVisit]);
};

export default useRecordRecentDetail;
